import Dialog from '@standardsdigital/ui-components/Dialog';
import IconButton from '@standardsdigital/ui-components/IconButton';
import type { VisibilityState } from '@standardsdigital/ui-components/Table';
import Table, { createColumnHelper } from '@standardsdigital/ui-components/Table';
import TextInput from '@standardsdigital/ui-components/TextInput';
import Expand from '@standardsdigital/ui-components/Expand';
import Button from '@standardsdigital/ui-components/Button';
import Icon from '@standardsdigital/ui-components/Icon';
import Select from '@standardsdigital/ui-components/Select';
import SelectItem from '@standardsdigital/ui-components/SelectItem';
import type { SubmitEventHandler, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CannotBeAddedReason,
  EformCount,
  LineSource,
  useAddProductToSubscriptionMutation,
  useSearchProductsQuery,
  ProductType,
} from 'redux/services/api.generated';
import styles from './ProductSearch.module.css';
import type { EnhancedProduct } from 'types/general';
import { UpdateStatus } from 'types/general';
import Loading from '@standardsdigital/ui-components/Loading';

interface ProductSearchProps {
  subscriptionId: string;
  isActiveSubscription: boolean;
  isEform: boolean;
}

interface Quantity {
  [productId: string]: EformCount;
}

const pageSize = 50;

const columnHelper = createColumnHelper<EnhancedProduct>();

const cannotBeAddedReasons = Object.values(CannotBeAddedReason);

// Cell components extracted outside to avoid unstable nested component warning
interface StatusCellProps {
  row: { original: EnhancedProduct };
}

function StatusCell({ row: { original: product } }: Readonly<StatusCellProps>) {
  const { t } = useTranslation();
  const { state, cannotBeAddedReason } = product;

  const warningReasons = cannotBeAddedReasons.filter(
    (reason) =>
      ![
        CannotBeAddedReason.AlreadyAdded,
        CannotBeAddedReason.PriceCalculationError,
      ].includes(reason),
  );
  const alreadyAdded =
    state === UpdateStatus.Success ||
    cannotBeAddedReason === CannotBeAddedReason.AlreadyAdded;
  const priceCalculationError =
    cannotBeAddedReason === CannotBeAddedReason.PriceCalculationError;
  const updateError = state === UpdateStatus.Error;

  if (alreadyAdded) {
    return <Icon name="Check" />;
  }

  if (priceCalculationError || updateError) {
    return <Icon name="XOctagon" className={styles.addProductError} />;
  }

  if (cannotBeAddedReason && warningReasons.includes(cannotBeAddedReason)) {
    return <Icon name="AlertTriangle" className={styles.addProductWarning} />;
  }

  if (state === UpdateStatus.Loading) {
    return <Loading size="small" aria-label={t('general.loading')} />;
  }

  return null;
}

function createQuantityCell(
  quantity: Quantity,
  setQuantity: React.Dispatch<React.SetStateAction<Quantity>>,
  eformCountValues: EformCount[],
) {
  return function QuantityCell({
    row: { original: product },
  }: {
    row: { original: EnhancedProduct };
  }) {
    const { t } = useTranslation();
    const { cannotBeAddedReason, id, state } = product;

    if (
      (cannotBeAddedReason && cannotBeAddedReasons.includes(cannotBeAddedReason)) ||
      typeof id !== 'string'
    ) {
      return null;
    }
    const productId = id ?? '';
    return (
      <Select
        selectSize="small"
        label={t('productSearch.selectQuantity')}
        invisibleLabel
        value={quantity[productId] ?? EformCount.$10}
        onChange={(event) => {
          setQuantity((prev) => ({
            ...prev,
            [productId]: Number(event.target.value),
          }));
        }}
        disabled={state === UpdateStatus.Success}
      >
        {eformCountValues.map((value) => (
          <SelectItem value={value} key={value}>
            {value}
          </SelectItem>
        ))}
      </Select>
    );
  };
}

function createActionsCell(
  quantity: Quantity,
  addProduct: ReturnType<typeof useAddProductToSubscriptionMutation>[0],
  subscriptionId: string,
  query: string | undefined,
  isEform: boolean,
) {
  return function ActionsCell({
    row: { original: product },
  }: {
    row: { original: EnhancedProduct };
  }) {
    const { t } = useTranslation();
    const { cannotBeAddedReason, cannotBeAddedMessage, state, id, productType } =
      product;

    const addProductSuccess = state === UpdateStatus.Success;
    const cannotBeAdded =
      cannotBeAddedReason && cannotBeAddedReasons.includes(cannotBeAddedReason);
    const isLoading = state === UpdateStatus.Loading;
    const productId = id ?? '';
    const hasErrorMessage =
      cannotBeAddedReason === CannotBeAddedReason.PriceCalculationError &&
      cannotBeAddedMessage;
    if (cannotBeAdded) {
      if (hasErrorMessage) {
        return (
          <Expand
            triggerButtonSize="small"
            triggerButtonVariant="text"
            triggerButtonText={t(
              `productSearch.cannotBeAddedReason.${cannotBeAddedReason}`,
            )}
            content={cannotBeAddedMessage}
          />
        );
      }
      return (
        <>
          {cannotBeAddedReason !== CannotBeAddedReason.AlreadyAdded && (
            <p>{t(`productSearch.cannotBeAddedReason.${cannotBeAddedReason}`)}</p>
          )}
        </>
      );
    }

    const onAddProduct = (lineSource: LineSource) => async () => {
      try {
        await addProduct({
          subscriptionId,
          addProductRequest: {
            productId: id ?? '',
            productType,
            lineSource,
            eformCount: isEform ? (quantity[productId] ?? EformCount.$10) : null,
          },
          // @ts-expect-error We are adding the query in the front end to control state.
          query,
        });
      } catch {
        // Errors are handled in middleware.
      }
    };

    return (
      <div className={styles.actions}>
        <Button
          size="small"
          disabled={addProductSuccess || isLoading}
          onClick={onAddProduct(LineSource.Manual)}
        >
          {t('productSearch.addAsManual')}
        </Button>
        <Button
          size="small"
          disabled={addProductSuccess || isLoading}
          onClick={onAddProduct(LineSource.Renewal)}
        >
          {t('productSearch.addAsRenewal')}
        </Button>
      </div>
    );
  };
}

function ProductSearch({
  subscriptionId,
  isActiveSubscription,
  isEform,
}: Readonly<ProductSearchProps>) {
  const { t, i18n } = useTranslation();

  const tableRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState<string | undefined>();

  const [quantity, setQuantity] = useState<Quantity>({});

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo(0, 0);
    }
  }, [query, tableRef]);

  let productTypes: ProductType[] = [
    ProductType.Standard,
    ProductType.Collection,
    ProductType.Publication,
  ];
  if (isEform) {
    productTypes = [ProductType.Eform];
  }

  const searchProducts = useSearchProductsQuery(
    {
      subscriptionId,
      query,
      productTypes,
      page,
      pageSize,
      language: i18n.language,
    },
    { skip: !query },
  );

  const eformCountValues = useMemo(
    () => Object.values(EformCount).filter((value) => typeof value === 'number'),
    [],
  );

  const { totalCount, items, hasNextPage } = useMemo(
    () =>
      searchProducts.data ?? {
        totalCount: 0,
        items: [],
        hasNextPage: false,
      },
    [searchProducts],
  );

  const hasData = items && items.length > 0;
  const [addProduct] = useAddProductToSubscriptionMutation();
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'status',
        header: 'Status',
        size: 80,
        cell: StatusCell,
      }),
      columnHelper.accessor('name', {
        header: t('productSearch.name'),
        size: 200,
      }),
      columnHelper.accessor('title', {
        header: t('productSearch.title'),
        size: 400,
      }),
      columnHelper.accessor('productType', {
        header: t('productSearch.type'),
        size: 120,
      }),
      columnHelper.display({
        id: 'quantity',
        header: t('productSearch.quantity'),
        cell: createQuantityCell(quantity, setQuantity, eformCountValues),
      }),
      columnHelper.display({
        id: 'actions',
        header: t('productSearch.actions'),
        size: 380,
        cell: createActionsCell(
          quantity,
          addProduct,
          subscriptionId,
          query,
          isEform,
        ),
      }),
    ],
    [addProduct, eformCountValues, isEform, quantity, query, subscriptionId, t],
  );

  const initialColumnVisibility: VisibilityState = useMemo(
    () => ({
      status: true,
      name: true,
      title: true,
      type: true,
      quantity: isEform,
      actions: true,
    }),
    [isEform],
  );

  const onFetchNextPage = useCallback(() => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [setPage, hasNextPage]);

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const searchValue = formData.get('search');
    const newQuery = typeof searchValue === 'string' ? searchValue : '';
    setQuery(newQuery);
    setPage(0);
  };

  const pagination = useMemo(
    () => ({
      onFetchNextPage,
      totalRowCount: totalCount ?? 0,
    }),
    [onFetchNextPage, totalCount],
  );

  const fetching = {
    isFetching: searchProducts.isFetching,
    isFetchingText: t('productSearch.loadingData'),
  };

  return (
    <>
      <IconButton
        icon="Plus"
        onClick={() => setOpen(true)}
        disabled={!isActiveSubscription}
      >
        {t('productSearch.addProduct')}
      </IconButton>
      <Dialog
        title={`${t('productSearch.addProductTitle', { subscriptionId })} ${
          isEform ? ` (${t('labels.eforms')})` : ''
        }`}
        open={open}
        onOpenChange={setOpen}
        closeButtonText={t('general.close')}
        className={styles.dialog}
        // Disable modal to avoid performance issues (see ticket CL-76).
        modal={false}
      >
        <form
          onSubmit={handleSubmit}
          className={`${styles.form} sd-search-box`}
          aria-labelledby="search-field"
        >
          <TextInput
            id="search-field"
            label={t('productSearch.searchLabel')}
            name="search"
          />
          <Button type="submit" variant="primary">
            {t('general.search')}
          </Button>
        </form>
        {typeof query === 'string' && !searchProducts.isLoading && (
          <p className={styles.count}>
            {t('productSearch.totalProducts', {
              totalCount,
            })}
          </p>
        )}
        {hasData ? (
          <Table<EnhancedProduct>
            columns={columns}
            data={searchProducts.data?.items ?? []}
            pagination={pagination}
            tableHeight="var(--table-height)"
            fetching={fetching}
            className={styles.table}
            aria-label={t('general.productSearch')}
            tableRef={tableRef as RefObject<HTMLDivElement>}
            initialState={{
              columnVisibility: initialColumnVisibility,
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}

export default ProductSearch;
